/**
 * Las pantallas de datos base (Fase 1).
 *
 * Se arman solas a partir de las definiciones que publica la API
 * (/api/maestros/definiciones): los rótulos, los tipos y las listas de
 * opciones están escritos una sola vez, del lado del servidor. Así no puede
 * pasar que la pantalla acepte algo que la validación rechaza.
 *
 * Reglas de la guía de diseño que se cumplen acá:
 *   * la plata a la derecha, en formato argentino
 *   * los km y las toneladas con su decimal
 *   * los estados con punto de color Y TEXTO, nunca sólo color
 *   * los campos con etiqueta visible, no texto gris adentro
 *   * las bajas piden confirmación y explican qué va a pasar
 */

import { formatearPesos, formatearFecha } from './formato.js';

const $ = (sel) => document.querySelector(sel);

let DEFINICIONES = null;

/**
 * Listas de otras fichas, para los campos que apuntan a otra tabla (por
 * ejemplo el camión habitual del chofer). Se traen una vez y se guardan.
 */
const LISTAS = new Map();

async function traerLista(pedir, ruta) {
  if (LISTAS.has(ruta)) return LISTAS.get(ruta);
  const r = await pedir(`/api/${ruta}`);
  const lista = r.ok ? (r.cuerpo[ruta] ?? []) : [];
  LISTAS.set(ruta, lista);
  return lista;
}

/** Se olvida lo guardado: al cambiar algo, la lista puede haber cambiado. */
export function olvidarListas() { LISTAS.clear(); }

export async function cargarDefiniciones(pedir) {
  if (DEFINICIONES) return DEFINICIONES;
  const r = await pedir('/api/maestros/definiciones');
  DEFINICIONES = r.ok ? r.cuerpo : {};
  return DEFINICIONES;
}

/** Cómo se muestra un valor según su tipo. */
function mostrar(valor, campo) {
  if (valor === null || valor === undefined || valor === '') return '—';
  switch (campo.tipo) {
    case 'referencia': {
      // Se muestra lo que la persona reconoce (la patente), no el número
      // interno. Si la ficha ya no está, se dice, en vez de mostrar un
      // identificador que no significa nada.
      const lista = LISTAS.get(campo.apuntaA) ?? [];
      const ficha = lista.find((f) => f.id === valor);
      return ficha ? String(ficha[campo.seMuestraCon] ?? '—') : '(ya no está)';
    }
    case 'centavos':  return formatearPesos(valor);
    case 'fecha':     return formatearFecha(valor);
    case 'booleano':  return valor ? 'Sí' : 'No';
    case 'opcion':    return campo.etiquetas?.[valor] ?? String(valor).replace(/_/g, ' ');
    default:          return String(valor);
  }
}

/**
 * Las opciones que puede tener un campo de referencia.
 *
 * Algunos campos apuntan a una parte del listado y no a todo: el acoplado
 * habitual sólo puede ser un remolcado. Sin esto, en la lista de acoplados
 * aparecerían los tractores, y elegir uno sería un error que recién avisa
 * la base al guardar.
 */
function listaDe(campo) {
  const lista = LISTAS.get(campo.apuntaA) ?? [];
  if (!campo.filtrarPor) return lista;
  return lista.filter((f) => f[campo.filtrarPor.campo] === campo.filtrarPor.valor);
}

/**
 * Muestra u oculta los campos que dependen de otro («acoplado habitual» no
 * tiene sentido en un acoplado) y VACÍA los que quedan ocultos: un campo que
 * no se ve no puede seguir mandando un valor a escondidas.
 *
 * Esto es comodidad de pantalla, no seguridad: lo que impide guardar un
 * disparate es la regla de la base.
 */
const condicion = (c) => c.soloSi
  ? ` data-solo-si="${c.soloSi.campo}" data-solo-si-valor="${c.soloSi.valor}"`
  : '';

/**
 * Los tildes que se deducen de otro campo.
 *
 * Elegir el tipo «semi» marca «Es un acoplado» solo. Sin esto, alguien elige
 * semi, no toca el tilde —que arranca en No—, y le queda un acoplado que el
 * sistema trata como camión: no aparece en la lista de acoplados de ningún
 * viaje y no hay forma de darse cuenta de por qué.
 *
 * Se aplica al CAMBIAR el campo del que depende, no todo el tiempo: así lo
 * que la persona elija después manda, y el sistema no le discute.
 */
function aplicarDeducciones(form, campos, cambiado) {
  for (const [nombre, c] of Object.entries(campos)) {
    if (!c.seDeduceDe || c.seDeduceDe.campo !== cambiado) continue;
    const origen = form.elements[c.seDeduceDe.campo];
    const destino = form.elements[nombre];
    if (!origen || !destino) continue;
    destino.value = c.seDeduceDe.valores.includes(origen.value) ? 'true' : 'false';
  }
}

function aplicarCamposCondicionales(form) {
  for (const caja of form.querySelectorAll('[data-solo-si]')) {
    const control = form.elements[caja.dataset.soloSi];
    const seVe = control ? String(control.value) === caja.dataset.soloSiValor : true;
    caja.hidden = !seVe;
    if (!seVe) {
      const propio = caja.querySelector('select, input');
      if (propio) propio.value = '';
    }
  }
}

const esNumerico = (campo) =>
  campo.tipo === 'centavos' || campo.tipo === 'decimal' || campo.tipo === 'entero';

/**
 * Las columnas del listado.
 *
 * Si la ficha marca campos con «enLista», se muestran ésos. Si no, las
 * primeras seis. Sirve para que un campo importante que está al final de la
 * ficha —como el camión habitual del chofer— igual se vea en la tabla.
 */
function columnasDelListado(campos, campoActivo) {
  const marcados = Object.entries(campos)
    .filter(([nombre, c]) => c.enLista && nombre !== campoActivo);
  if (marcados.length > 0) return marcados;
  return Object.entries(campos)
    .filter(([nombre]) => nombre !== campoActivo)
    .slice(0, 6);
}

function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

/**
 * Dibuja una pantalla de datos base completa: listado + formulario.
 */
export function pantallaDeMaestro(contenedor, opciones) {
  const { ruta, definicion, pedir } = opciones;
  const { campos, campoActivo, singular, plural } = definicion;

  let verTodos = false;
  let editando = null;

  const cabecera = document.createElement('div');
  cabecera.style.cssText = 'display:flex;gap:12px;align-items:center;margin-bottom:14px;flex-wrap:wrap';
  cabecera.innerHTML = `
    <button class="boton principal" data-accion="nuevo">＋ Nuevo</button>
    <label style="display:flex;align-items:center;gap:7px;font-size:13.5px;color:var(--texto-2)">
      <input type="checkbox" data-accion="ver-todos"> Mostrar también los dados de baja
    </label>
    <span class="separador" style="flex:1"></span>
    <span data-rol="cantidad" style="color:var(--texto-suave);font-size:13px"></span>`;
  contenedor.appendChild(cabecera);

  const avisos = document.createElement('div');
  contenedor.appendChild(avisos);

  const panelFormulario = document.createElement('div');
  panelFormulario.hidden = true;
  contenedor.appendChild(panelFormulario);

  const panelTabla = document.createElement('div');
  panelTabla.className = 'tarjeta';
  contenedor.appendChild(panelTabla);

  // --- Listado -------------------------------------------------------
  async function dibujarTabla() {
    // Las listas de las fichas a las que apunta esta pantalla (por ejemplo
    // los camiones, para el camión habitual del chofer).
    for (const c of Object.values(campos)) {
      if (c.tipo === 'referencia' && c.apuntaA) await traerLista(pedir, c.apuntaA);
    }
    const r = await pedir(`/api/${ruta}${verTodos ? '?activos=todos' : ''}`);
    if (!r.ok) {
      panelTabla.innerHTML = '';
      panelTabla.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo traer la lista.'));
      return;
    }
    const filas = r.cuerpo[ruta] ?? [];
    cabecera.querySelector('[data-rol="cantidad"]').textContent =
      filas.length === 1 ? `1 ${singular.replace(/^(el|la) /, '')}` : `${filas.length} ${plural}`;

    if (filas.length === 0) {
      panelTabla.innerHTML =
        `<p style="color:var(--texto-suave);text-align:center;padding:26px 0;margin:0">
           Todavía no se cargó ningún registro. Empezá con el botón «Nuevo».</p>`;
      return;
    }

    const cols = columnasDelListado(campos, campoActivo);
    const encabezados = cols.map(([, c]) =>
      `<th class="${esNumerico(c) ? (c.tipo === 'centavos' ? 'plata' : 'numero') : ''}">${c.rotulo}</th>`).join('');

    const cuerpo = filas.map((fila) => {
      const celdas = cols.map(([nombre, c]) =>
        `<td class="${esNumerico(c) ? (c.tipo === 'centavos' ? 'plata' : 'numero') : ''}">${
          mostrar(fila[nombre], c)}</td>`).join('');
      const activo = campoActivo ? fila[campoActivo] : true;
      const estado = activo
        ? '<span class="etiqueta bien">En uso</span>'
        : '<span class="etiqueta">Dado de baja</span>';
      return `<tr data-id="${fila.id}">${celdas}<td>${estado}</td>
        <td style="text-align:right;white-space:nowrap">
          <button class="boton" data-editar="${fila.id}">Modificar</button>
        </td></tr>`;
    }).join('');

    panelTabla.innerHTML = `<div class="tabla-scroll"><table class="datos">
      <thead><tr>${encabezados}<th>Estado</th><th></th></tr></thead>
      <tbody>${cuerpo}</tbody></table></div>`;

    panelTabla.querySelectorAll('[data-editar]').forEach((b) => {
      b.addEventListener('click', () => {
        const fila = filas.find((f) => f.id === b.dataset.editar);
        abrirFormulario(fila);
      });
    });
  }

  // --- Formulario ----------------------------------------------------
  function abrirFormulario(fila) {
    editando = fila ?? null;
    const titulo = fila ? `Modificar ${singular}` : `Nuevo — ${singular}`;

    const entradas = Object.entries(campos).filter(([nombre]) => {
      // AL DAR DE ALTA no se muestra el tilde de "está en uso". Una ficha
      // nueva está en uso: para eso se está cargando.
      //
      // EL DÍA QUE PASÓ (17/10/2026): el cliente cargó dos clientes y
      // desaparecieron. Ese campo ("Opera", en clientes) era el último de
      // cinco desplegables seguidos —dador de carga, retiene IVA, retiene
      // Ganancias, retiene Ingresos Brutos, Opera— y él contestó el patrón:
      // No, No, No, No... y No. El cliente se creó DADO DE BAJA y no apareció
      // en la lista. Volvió a cargarlo y le dijo "ya existe ese CUIT".
      // Desde afuera: "cargo clientes y no pasa nada".
      //
      // Al MODIFICAR sí se muestra, que es donde tiene sentido. Y para dar
      // de baja está el botón, que dice lo que hace.
      return !(!fila && nombre === campoActivo);
    }).map(([nombre, c]) => {
      const valor = fila?.[nombre] ?? '';
      const id = `campo-${nombre}`;
      if (c.tipo === 'booleano') {
        // En una ficha nueva no hay valor, y el navegador elige la primera
        // opción: por eso «En servicio» viene en Sí. Pero hay campos donde
        // ese Sí sería un error grave —«Es un acoplado» dejaría a todos los
        // camiones convertidos en acoplados—, así que la ficha puede pedir
        // otro valor de arranque con porDefecto.
        const elegido = typeof valor === 'boolean'
          ? valor
          : (c.porDefecto === undefined ? true : c.porDefecto === 'true');
        return `<div class="campo"${condicion(c)}>
          <label for="${id}">${c.rotulo}</label>
          <select id="${id}" name="${nombre}">
            <option value="true"  ${elegido === true  ? 'selected' : ''}>Sí</option>
            <option value="false" ${elegido === false ? 'selected' : ''}>No</option>
          </select></div>`;
      }
      if (c.tipo === 'referencia') {
        const lista = listaDe(c);
        const opts = lista.map((f) =>
          `<option value="${f.id}" ${valor === f.id ? 'selected' : ''}>${
            f[c.seMuestraCon] ?? f.id}</option>`).join('');
        const ayuda = lista.length === 0
          ? '<div class="ayuda">Todavía no hay ninguno cargado que sirva acá.</div>'
          : '<div class="ayuda">Es sólo una sugerencia: al cargar un viaje viene ' +
            'precargado y se puede cambiar sin problema.</div>';
        return `<div class="campo"${condicion(c)}>
          <label for="${id}">${c.rotulo}</label>
          <select id="${id}" name="${nombre}">
            <option value="">— ninguno —</option>${opts}</select>
          ${ayuda}</div>`;
      }
      if (c.tipo === 'opcion') {
        // Si el campo trae un valor sugerido y la ficha es nueva, viene ya
        // elegido: así no se manda un vacío que la base no admite.
        const elegido = valor !== '' && valor !== null && valor !== undefined
          ? String(valor) : (fila ? '' : (c.porDefecto ?? ''));
        // El rótulo que declara la ficha si lo hay; si no, el valor con los
        // guiones bajos convertidos en espacios.
        const comoSeLee = (o) => c.etiquetas?.[o] ?? o.replace(/_/g, ' ');
        const opts = (c.opciones ?? []).map((o) =>
          `<option value="${o}" ${elegido === o ? 'selected' : ''}>${comoSeLee(o)}</option>`).join('');
        const vacia = elegido === ''
          ? '<option value="" selected>— elegir —</option>' : '<option value="">— elegir —</option>';
        return `<div class="campo">
          <label for="${id}">${c.rotulo}${c.requerido ? ' *' : ''}</label>
          <select id="${id}" name="${nombre}">${vacia}${opts}</select></div>`;
      }
      // 'cuit' se escribe como texto: quien carga puede ponerle guiones. El
      // servidor lo comprueba y lo guarda sólo con números.
      const tipoHtml = c.tipo === 'fecha' ? 'date' : 'text';
      const ayuda = c.tipo === 'centavos'
        ? '<div class="ayuda">En pesos. Se guarda al centavo.</div>'
        : c.tipo === 'decimal' ? '<div class="ayuda">Se admite coma o punto.</div>'
        : c.tipo === 'cuit'
          ? '<div class="ayuda">Con guiones o sin ellos. Se comprueba que exista.</div>' : '';
      const valorMostrado = c.tipo === 'centavos' && valor !== '' && valor !== null
        ? (Number(BigInt(valor) / 100n)).toString()
        : (valor ?? '');
      return `<div class="campo">
        <label for="${id}">${c.rotulo}${c.requerido ? ' *' : ''}</label>
        <input type="${tipoHtml}" id="${id}" name="${nombre}" value="${valorMostrado}">
        ${ayuda}</div>`;
    }).join('');

    panelFormulario.hidden = false;
    panelFormulario.className = 'tarjeta';
    panelFormulario.style.marginBottom = '16px';
    panelFormulario.innerHTML = `
      <h2 style="font-size:16px;margin:0 0 4px">${titulo}</h2>
      <p style="color:var(--texto-suave);font-size:13px;margin:0 0 16px">
        Los campos con * son obligatorios.</p>
      <div data-rol="errores"></div>
      <!-- method="post" aunque lo maneje el JavaScript: si el JavaScript no
           carga, el navegador NO puede mandar los datos por la URL. -->
      <form method="post" action="/api/${ruta}" novalidate>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:0 16px">
          ${entradas}
        </div>
        <div style="display:flex;gap:10px;margin-top:14px">
          <button type="submit" class="boton principal">Guardar</button>
          <button type="button" class="boton" data-accion="cancelar">Cancelar</button>
          ${fila && campoActivo && fila[campoActivo]
            ? '<button type="button" class="boton" data-accion="baja" style="margin-left:auto">Dar de baja</button>'
            : ''}
        </div>
      </form>`;

    panelFormulario.querySelector('[data-accion="cancelar"]').addEventListener('click', cerrar);
    const botonBaja = panelFormulario.querySelector('[data-accion="baja"]');
    if (botonBaja) botonBaja.addEventListener('click', () => darDeBaja(fila));
    const formulario = panelFormulario.querySelector('form');
    formulario.addEventListener('submit', guardar);
    formulario.addEventListener('change', (e) => {
      if (e.target?.name) aplicarDeducciones(formulario, campos, e.target.name);
      aplicarCamposCondicionales(formulario);
    });
    aplicarCamposCondicionales(formulario);
    panelFormulario.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function cerrar() {
    panelFormulario.hidden = true;
    panelFormulario.innerHTML = '';
    editando = null;
  }

  function leerFormulario(form) {
    const datos = {};
    for (const [nombre, c] of Object.entries(campos)) {
      const control = form.elements[nombre];
      if (!control) continue;
      // Un campo oculto por su condición manda vacío, no lo que tuviera
      // cargado antes de ocultarse.
      const caja = control.closest('[data-solo-si]');
      let v = caja && caja.hidden ? '' : control.value;
      if (c.tipo === 'centavos' && v !== '') {
        // La plata se escribe en pesos y viaja en centavos enteros.
        const limpio = String(v).replace(/\./g, '').replace(',', '.').trim();
        if (!/^-?\d+(\.\d{1,2})?$/.test(limpio)) { datos[nombre] = v; continue; }
        const [ent, dec = ''] = limpio.split('.');
        v = `${ent}${dec.padEnd(2, '0')}`;
      }
      datos[nombre] = v;
    }
    return datos;
  }

  async function guardar(evento) {
    evento.preventDefault();
    const form = evento.target;
    const cajaErrores = panelFormulario.querySelector('[data-rol="errores"]');
    cajaErrores.innerHTML = '';

    // Desde acá hasta el final, CUALQUIER cosa que salga mal tiene que
    // terminar en un cartel. Antes, si esto reventaba, la promesa quedaba
    // rechazada en silencio y la persona veía que el botón no hacía nada.
    const boton = form.querySelector('button[type="submit"]');
    try {
      boton.disabled = true;
      await guardarDeVerdad(form, cajaErrores);
    } catch (error) {
      cajaErrores.appendChild(alerta('critico',
        `No se pudo guardar por un problema del sistema: ${error.message}. ` +
        'Ya quedó avisado. Tus datos siguen en pantalla: no los borres.'));
      cajaErrores.scrollIntoView({ behavior: 'smooth', block: 'center' });
      throw error;     // para que además quede anotado en el servidor
    } finally {
      boton.disabled = false;
    }
  }

  async function guardarDeVerdad(form, cajaErrores) {
    const datos = leerFormulario(form);
    const r = editando
      ? await pedir(`/api/${ruta}/${editando.id}`, { method: 'PUT', body: JSON.stringify(datos) })
      : await pedir(`/api/${ruta}`, { method: 'POST', body: JSON.stringify(datos) });

    if (r.ok) {
      cerrar();
      olvidarListas();     // lo que se acaba de guardar puede cambiar las listas
      avisos.innerHTML = '';
      avisos.appendChild(alerta('bien', editando ? 'Cambios guardados.' : 'Guardado.'));
      setTimeout(() => { avisos.innerHTML = ''; }, 4000);
      await dibujarTabla();
      return;
    }
    if (r.cuerpo?.errores) {
      for (const e of r.cuerpo.errores) {
        cajaErrores.appendChild(alerta('critico', `${e.rotulo}: ${e.problema}`));
      }
    } else {
      cajaErrores.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo guardar.'));
    }
  }

  async function darDeBaja(fila) {
    // Explica qué va a pasar antes de hacerlo, y aclara que no se borra.
    const nombre = fila.patente ?? fila.razon_social ?? fila.nombre ?? 'este registro';
    const seguro = window.confirm(
      `¿Dar de baja «${nombre}»?\n\n` +
      'Va a dejar de aparecer en las listas para elegir, pero NO se borra: ' +
      'los viajes y los gastos que ya lo usan siguen intactos, y se puede ' +
      'volver a habilitar cuando quieras.');
    if (!seguro) return;
    const r = await pedir(`/api/${ruta}/${fila.id}`,
      { method: 'PUT', body: JSON.stringify({ [campoActivo]: 'false' }) });
    if (r.ok) { cerrar(); olvidarListas(); await dibujarTabla(); }
    else {
      avisos.innerHTML = '';
      avisos.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo dar de baja.'));
    }
  }

  cabecera.querySelector('[data-accion="nuevo"]')
    .addEventListener('click', () => abrirFormulario(null));
  cabecera.querySelector('[data-accion="ver-todos"]')
    .addEventListener('change', (e) => { verTodos = e.target.checked; void dibujarTabla(); });

  void dibujarTabla();
}
